import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, Users, CreditCard, Shield } from 'lucide-react';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-accent-text hover:underline mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </Link>

          <div className="text-center">
            <Scale className="mx-auto h-12 w-12 text-accent-text mb-4" />
            <h1 className="text-3xl font-bold text-foreground">Terms of Service</h1>
            <p className="mt-2 text-lg text-muted-foreground">
              Agreement governing your use of the Enterprise API Gateway
            </p>
            <p className="text-sm text-muted-foreground mt-2">Last updated: October 2026</p>
            <p className="text-sm text-muted-foreground mt-1">
              Governed by the laws of the Federal Republic of Nigeria
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-surface rounded-lg shadow-sm border border-border p-8 prose prose-lg max-w-none space-y-8">

          {/* 1. Parties */}
          <section>
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">1. Parties and Agreement</h2>
            </div>
            <p className="text-muted-foreground">
              These Terms of Service ("Terms") constitute a legally binding agreement between
              <strong> Latlens Technologies</strong> ("Company", "we", "us", "our"), a business
              registered in Nigeria with its principal place of business at <strong>38b, Ondo Street,
              New Bodija, Ibadan, Oyo State, Nigeria</strong>, and you ("User", "you", "your")
              — the individual or legal entity accessing or using the Enterprise API Gateway and
              related services (collectively, the "Service").
            </p>
            <p className="text-muted-foreground mt-3">
              By registering for an account, accessing the Service, or clicking "I agree", you
              acknowledge that you have read, understood, and agree to be bound by these Terms and
              our <Link to="/privacy" className="text-accent-text hover:underline">Privacy Policy</Link>.
              If you do not agree, you must not access or use the Service.
            </p>
          </section>

          {/* 2. Eligibility */}
          <section>
            <div className="flex items-center mb-4">
              <Users className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">2. Eligibility and Account Registration</h2>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-4">2.1 Eligibility</h3>
            <p className="text-muted-foreground">
              You must give your date of birth when you create an account. If you are 18 or over, you
              must be legally capable of entering into a binding contract under Nigerian law. If you are
              under 18, you may use the Service only with the permission of your parent or legal
              guardian, who accepts these Terms on your behalf and is responsible for your use of the
              Service, including any payments. Until they give permission, your account cannot be used;
              if they refuse, or do not answer within 7 days, we delete it. If you are acting on behalf
              of a legal entity, you represent that you are 18 or over and have authority to bind that
              entity to these Terms.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">2.2 Account Information</h3>
            <p className="text-muted-foreground">
              You must provide accurate, current, and complete information during registration and
              keep your account information updated. Providing false information is a material
              breach of these Terms and grounds for immediate account termination.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">2.3 Account Security</h3>
            <p className="text-muted-foreground">
              You are responsible for maintaining the confidentiality of your login credentials
              and API keys, and for all activities that occur under your account. You must notify
              us immediately at{' '}
              <a href="mailto:support@latlens.com" className="text-accent-text hover:underline">
                support@latlens.com
              </a>{' '}
              if you become aware of any unauthorised access. We shall not be liable for any loss
              arising from your failure to secure your credentials.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">2.4 One Account Per User</h3>
            <p className="text-muted-foreground">
              Each individual may maintain only one personal account. Corporate accounts may be
              granted team access at our discretion. Account sharing with third parties is prohibited.
            </p>
          </section>

          {/* 3. Acceptable Use */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">3. Acceptable Use Policy</h2>
            </div>
            <p className="text-muted-foreground">
              You agree to use the Service only for lawful purposes in compliance with these Terms,
              our Acceptable Use Policy, and all applicable Nigerian and international laws. You
              must not use the Service to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li>Violate any applicable federal, state, or local law or regulation of Nigeria, or any applicable international law</li>
              <li>Process, transmit, or store personal data in violation of the Nigeria Data Protection Act 2023 (NDPA) or Nigeria Data Protection Regulation 2019 (NDPR)</li>
              <li>Attempt unauthorised access to our systems, other users' accounts, or any connected network</li>
              <li>Introduce malware, viruses, ransomware, trojans, or any other malicious code</li>
              <li>Conduct denial-of-service attacks, port scanning, or any form of network abuse</li>
              <li>Harvest, scrape, or collect personal data about third parties without a lawful basis</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
              <li>Use the Service for any activity that facilitates money laundering, terrorism financing, or other criminal activity as defined under the Money Laundering (Prevention and Prohibition) Act 2022</li>
              <li>Circumvent rate limits, abuse the Service, or use the API in a manner that places unreasonable load on our infrastructure</li>
              <li>Reverse-engineer, decompile, or disassemble any part of the Service</li>
              <li>Resell, sublicense, or commercially exploit the Service without our prior written consent</li>
            </ul>
          </section>

          {/* 4. API Usage */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">4. API Access and Usage</h2>

            <h3 className="text-lg font-semibold text-foreground mt-4">4.1 API Keys</h3>
            <p className="text-muted-foreground">
              Upon registration, you may generate API keys to authenticate requests. You are solely
              responsible for keeping your API keys confidential. You must not embed API keys in
              client-side code, public repositories, or any publicly accessible medium. Suspected
              compromised keys must be rotated immediately.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">4.2 Rate Limits</h3>
            <p className="text-muted-foreground">
              Your API access is subject to rate limits set on each API key (requests per minute,
              per hour, and per day). Exceeding rate limits will result in temporary
              throttling. Repeated or intentional abuse of rate limits may result in account
              suspension.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">4.3 Data You Submit via the API</h3>
            <p className="text-muted-foreground">
              You warrant that you have all necessary rights, consents, and authorisations to
              process any data transmitted through the API, and that such processing complies with
              the NDPA 2023, NDPR 2019, and any other applicable data protection law. You indemnify
              us against any claims arising from your failure to comply with this warranty.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">4.4 Service Modifications</h3>
            <p className="text-muted-foreground">
              We reserve the right to modify, deprecate, or discontinue any API endpoint or feature.
              We will provide at least 30 days' notice for material deprecations, except where
              required immediately for security or legal reasons.
            </p>
          </section>

          {/* 5. Billing */}
          <section>
            <div className="flex items-center mb-4">
              <CreditCard className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">5. Billing, Payments, and Credits</h2>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-4">5.1 Pay-As-You-Go Pricing</h3>
            <p className="text-muted-foreground">
              The Service is offered on a pay-as-you-go basis with prepaid credits. There are no
              subscription plans or recurring charges. Each API product includes a free monthly
              allowance; use beyond it is charged per request, tile, or other unit from your
              prepaid credits. Current pricing is shown on the Billing page of your account
              dashboard. All prices are quoted in Nigerian Naira (NGN) and are exclusive of
              applicable taxes.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">5.2 Payment Processing</h3>
            <p className="text-muted-foreground">
              Payments are processed by Paystack, a PCI-DSS compliant payment processor licensed
              by the Central Bank of Nigeria. By providing payment details, you authorise us to
              charge the applicable fees. We do not store card numbers or bank details.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">5.3 Refund Policy</h3>
            <p className="text-muted-foreground">
              Prepaid credits are non-refundable except: (a) where required by applicable Nigerian
              consumer protection law; or (b) where we have materially failed to provide the
              Service for more than 72 consecutive hours. Prepaid credits remain valid for 12
              months from purchase.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">5.4 Price Changes</h3>
            <p className="text-muted-foreground">
              We reserve the right to change pricing with at least 30 days' notice by email.
              Price changes apply to usage from the effective date stated in the notice. Continued use
              of the Service after the effective date constitutes acceptance of the new pricing.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">5.5 Insufficient Credits</h3>
            <p className="text-muted-foreground">
              Because the Service is prepaid, no amount becomes overdue. When your free allowance
              is used up and your credits are insufficient for a request, that request is refused
              until you buy more credits.
            </p>
          </section>

          {/* 6. Service Availability */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">6. Service Availability and SLA</h2>

            <h3 className="text-lg font-semibold text-foreground mt-4">6.1 Uptime</h3>
            <p className="text-muted-foreground">
              We target 99.5% monthly uptime for the API Gateway. Planned maintenance will be
              announced at least 24 hours in advance via email or the Service dashboard. Emergency
              maintenance required for security reasons may be conducted without prior notice.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">6.2 No Guarantee</h3>
            <p className="text-muted-foreground">
              We do not guarantee uninterrupted, error-free, or completely secure access to the
              Service. Downtime caused by force majeure events (including internet infrastructure
              failures, government action, or acts of God) is excluded from SLA calculations.
            </p>
          </section>

          {/* 7. Privacy and Data Protection */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">7. Data Protection and Privacy</h2>
            <p className="text-muted-foreground">
              We process personal data in accordance with the Nigeria Data Protection Act 2023
              (NDPA), the Nigeria Data Protection Regulation 2019 (NDPR), and our{' '}
              <Link to="/privacy" className="text-accent-text hover:underline">Privacy Policy</Link>,
              which is incorporated into and forms part of these Terms. By using the Service,
              you acknowledge the terms of our Privacy Policy.
            </p>
            <p className="text-muted-foreground mt-3">
              Where you use the Service to process personal data of third parties (your own users
              or customers), you act as a Data Controller for such data, and you are solely
              responsible for ensuring that your processing complies with all applicable data
              protection laws. We act as a Data Processor for such data and will process it only
              on your documented instructions.
            </p>
            <p className="text-muted-foreground mt-3">
              Upon request, we will enter into a Data Processing Agreement (DPA) with you in
              accordance with Section 43 of the NDPA 2023. Contact{' '}
              <a href="mailto:dpo@latlens.com" className="text-accent-text hover:underline">dpo@latlens.com</a>{' '}
              to request a DPA.
            </p>
          </section>

          {/* 8. Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">8. Intellectual Property</h2>
            <p className="text-muted-foreground">
              The Service, its software, documentation, logos, trade marks, and all related
              intellectual property are and remain the exclusive property of Latlens Technologies
              and its licensors, protected under Nigerian copyright law (Copyright Act 2022),
              trade mark law, and applicable international conventions.
            </p>
            <p className="text-muted-foreground mt-3">
              You retain all ownership rights in data and content you submit through the Service.
              By using the Service, you grant us a non-exclusive, worldwide, royalty-free licence
              to process, copy, and transmit your data solely to the extent necessary to provide
              the Service.
            </p>
            <p className="text-muted-foreground mt-3">
              You may not copy, reproduce, distribute, modify, reverse-engineer, or create
              derivative works from any part of the Service without our prior written consent.
            </p>
          </section>

          {/* 9. Disclaimers */}
          <section>
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">9. Disclaimers and Limitation of Liability</h2>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-4">9.1 "As Is" Service</h3>
            <p className="text-muted-foreground">
              The Service is provided "as is" and "as available" without warranty of any kind,
              whether express or implied, including implied warranties of merchantability, fitness
              for a particular purpose, or non-infringement, to the maximum extent permitted by
              Nigerian law.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">9.2 Limitation of Liability</h3>
            <p className="text-muted-foreground">
              To the maximum extent permitted by the laws of the Federal Republic of Nigeria,
              Latlens Technologies shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including loss of profits, data, business
              opportunity, goodwill, or other intangible losses, arising out of or related to
              your use of or inability to use the Service.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">9.3 Cap on Liability</h3>
            <p className="text-muted-foreground">
              Our total aggregate liability to you for any claims arising from or relating to
              these Terms or the Service shall not exceed the total fees paid by you to us in
              the three (3) months immediately preceding the event giving rise to the claim.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">9.4 Indemnification</h3>
            <p className="text-muted-foreground">
              You agree to indemnify, defend, and hold harmless Latlens Technologies and its
              officers, employees, and agents from any claims, liabilities, damages, and costs
              (including reasonable legal fees) arising from: (a) your violation of these Terms;
              (b) your use of the Service; (c) your violation of any third-party right; or
              (d) your breach of applicable data protection law.
            </p>
          </section>

          {/* 10. Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">10. Suspension and Termination</h2>

            <h3 className="text-lg font-semibold text-foreground mt-4">10.1 Termination by You</h3>
            <p className="text-muted-foreground">
              You may terminate your account at any time through your account dashboard or by
              emailing{' '}
              <a href="mailto:support@latlens.com" className="text-accent-text hover:underline">
                support@latlens.com
              </a>.
              Termination takes effect immediately. We recommend
              downloading your data before termination.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">10.2 Termination by Us</h3>
            <p className="text-muted-foreground">
              We may suspend or terminate your account immediately and without prior notice if:
              (a) you materially breach these Terms; (b) we are required to do so by law or a
              competent regulatory authority; (c) you engage in fraudulent or abusive activity;
              or (d) your account poses a security risk to our infrastructure or other users.
              We will provide notice where legally permitted to do so.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">10.3 Effect of Termination</h3>
            <p className="text-muted-foreground">
              Upon termination, your access to the Service ceases immediately. We will retain
              and delete your data in accordance with our Privacy Policy and applicable law.
              Provisions of these Terms that by their nature should survive termination
              (including intellectual property, limitation of liability, and dispute resolution)
              shall remain in force.
            </p>
          </section>

          {/* 11. Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">11. Governing Law and Dispute Resolution</h2>

            <h3 className="text-lg font-semibold text-foreground mt-4">11.1 Governing Law</h3>
            <p className="text-muted-foreground">
              These Terms and any dispute or claim arising out of or in connection with them shall
              be governed by and construed in accordance with the laws of the Federal Republic of
              Nigeria, including the Nigeria Data Protection Act 2023, without regard to conflict
              of law principles.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">11.2 Amicable Resolution</h3>
            <p className="text-muted-foreground">
              In the event of a dispute, both parties agree to first attempt resolution through
              good-faith negotiation for at least 30 days before initiating formal proceedings.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">11.3 Arbitration</h3>
            <p className="text-muted-foreground">
              If amicable resolution fails, disputes shall be referred to and finally resolved by
              arbitration under the Arbitration and Mediation Act 2023 of Nigeria. The seat of
              arbitration shall be Ibadan, Oyo State, Nigeria. The language of arbitration shall
              be English. The decision of the arbitrator shall be final and binding.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">11.4 Jurisdiction</h3>
            <p className="text-muted-foreground">
              Notwithstanding the foregoing, either party may seek urgent injunctive or interim
              relief from a court of competent jurisdiction in Nigeria. Each party submits to the
              exclusive jurisdiction of the courts of Oyo State for this purpose.
            </p>
          </section>

          {/* 12. General */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">12. General Provisions</h2>

            <h3 className="text-lg font-semibold text-foreground mt-4">12.1 Entire Agreement</h3>
            <p className="text-muted-foreground">
              These Terms, together with the Privacy Policy and any applicable order forms or
              Data Processing Agreement, constitute the entire agreement between you and us
              regarding the Service and supersede all prior agreements and understandings.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">12.2 Amendments</h3>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. Material changes will be
              notified by email and/or prominent notice on the Service at least 30 days before
              taking effect. Continued use of the Service after the effective date constitutes
              acceptance.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">12.3 Severability</h3>
            <p className="text-muted-foreground">
              If any provision of these Terms is found invalid or unenforceable by a competent
              court or arbitrator, the remaining provisions shall continue in full force and effect.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">12.4 Waiver</h3>
            <p className="text-muted-foreground">
              No failure or delay by us in exercising any right or remedy shall constitute a
              waiver of that right or remedy or of any other right or remedy.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">12.5 Assignment</h3>
            <p className="text-muted-foreground">
              You may not assign or transfer your rights or obligations under these Terms without
              our prior written consent. We may assign these Terms without restriction in connection
              with a merger, acquisition, or sale of assets.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">12.6 Force Majeure</h3>
            <p className="text-muted-foreground">
              We shall not be liable for any delay or failure to perform our obligations caused by
              circumstances beyond our reasonable control, including acts of God, government action,
              national grid failure, or major internet infrastructure outages.
            </p>
          </section>

          {/* Contact */}
          <section className="p-6 bg-accent/10 rounded-lg border border-accent/30">
            <h2 className="text-2xl font-semibold text-foreground">13. Contact Information</h2>
            <p className="text-muted-foreground mt-2 mb-4">
              For legal enquiries, disputes, or questions about these Terms:
            </p>
            <div className="space-y-2 text-muted-foreground">
              <p><strong>Legal / Compliance:</strong> <a href="mailto:legal@latlens.com" className="text-accent-text hover:underline">legal@latlens.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@latlens.com" className="text-accent-text hover:underline">support@latlens.com</a></p>
              <p><strong>Registered Address:</strong> Latlens Technologies, 38b, Ondo Street, New Bodija, Ibadan, Oyo State, Nigeria</p>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Acknowledgement:</strong> By using the Service, you confirm that you have
                read, understood, and agree to be bound by these Terms of Service and our Privacy
                Policy, and that your use of the Service is in compliance with all applicable Nigerian
                laws, including the Nigeria Data Protection Act 2023.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
